import { AsyncPipe } from '@angular/common';
import { AfterContentInit, Component, ElementRef, OnDestroy, OnInit, Signal, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { skip, switchMap, take } from 'rxjs/operators';
import { Banner, Link } from 'src/app/models';
import { HomeType, ProductSection } from 'src/app/models/home';
import { AuthService } from 'src/app/services/auth';
import { BannerService } from 'src/app/services/banner.service';
import { CategoryService } from 'src/app/services/category/category.service';
import { ContentService } from 'src/app/services/content/content.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { ProductQuery, ProductService } from 'src/app/services/product';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { RichTextBoxComponent } from 'src/app/shared/components';
import { BannerComponent, FullBannerComponent } from 'src/app/shared/components/banner';
import { HeroBannerComponent } from 'src/app/shared/components/hero-banner/hero-banner.component';
import { ProductCardComponent } from 'src/app/shared/components/product/card';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-home-simple',
  templateUrl: './home-simple.component.html',
  styleUrls: ['./home-simple.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HeroBannerComponent, BannerComponent, RichTextBoxComponent, RouterLink, ProductCardComponent, AsyncPipe, FullBannerComponent],
})
export class HomeSimpleComponent extends ContentSection implements OnInit, AfterContentInit, OnDestroy {
  private productQuery = inject(ProductQuery);
  private productService = inject(ProductService);
  private categoryService = inject(CategoryService);
  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private kioskService = inject(KioskService);
  private bannerService = inject(BannerService);

  private subscription: Subscription;

  primarySection: ProductSection;
  secondarySections: ProductSection[];

  heroBanners: Signal<Banner[]> = signal([]);
  topTextBanner: string;
  bottomTextBanner: string;
  bottomBanner: Partial<Banner>;

  filterMenu: { categoryTitle: string; allProducts: string };
  openCategoriesMenu: string;

  loading: Observable<boolean>;
  isKiosk: boolean;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, HomeType.simple, { meta: true });

    this.topTextBanner = this.content.get('textBannerTop');
    this.bottomBanner = {
      image: this.content.get('campaign2BannerImage'),
      link: this.content.get('campaign2BannerUrl'),
      title: this.content.get('campaign2BannerName'),
    };
    this.bottomTextBanner = this.content.get('textBannerBottom');

    const productsOverviewContent = this.contentService.get('productsOverview');
    this.filterMenu = {
      categoryTitle: productsOverviewContent.get('categoryLabelText'),
      allProducts: productsOverviewContent.get('allProductsFilterText'),
    };
    this.openCategoriesMenu = productsOverviewContent.get('openCategoriesMenuText');

    this.heroBanners = toSignal(this.bannerService.getHeroBanners(this.contentService.getMerchantId()));
  }

  ngOnInit() {
    this.subscription = new Subscription();
    this.isKiosk = this.kioskService.isKiosk;

    // CL Party Service global shop hide products
    if (this.contentService.getMerchant().id === 169) return;

    this.loading = this.productQuery.selectLoading();
    this.initFrontPageProducts();

    this.subscription.add(
      this.authService.member.pipe(skip(1)).subscribe(() => {
        this.initFrontPageProducts();
      }),
    );
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  initFrontPageProducts() {
    this.categoryService
      .getFrontpage()
      .pipe(take(1))
      .subscribe(async (frontpage) => {
        const categories = [...frontpage];
        const primaryCategory = categories.shift();
        if (primaryCategory) {
          await this.productService.getFrontpageProducts(primaryCategory.frontPageProductsCount, primaryCategory.id);
          const link: Link = {
            url: `${environment.urls.products}`,
            params: null,
            name: this.content.get('seeAllLocalOffers'), // TODO: how to name this?
          };
          if (!primaryCategory.hidden) {
            link.url = `${environment.urls.products}/${primaryCategory.categoryUrl}`;
          }

          const productSection: ProductSection = {
            title: primaryCategory.name,
            products: this.productQuery.selectFrontpageProducts$.pipe(switchMap((ids) => this.productQuery.selectMany(ids[0]))),
            link,
          };

          this.primarySection = productSection;
        }

        this.secondarySections = [];
        if (!categories.length) {
          return;
        }

        for (const [i, category] of categories.entries()) {
          await this.productService.getFrontpageProducts(category.frontPageProductsCount, category.id);

          const link: Link = {
            url: `${environment.urls.products}/${category.categoryUrl}`,
            params: null,
            name: category.name,
          };

          const productSection: ProductSection = {
            title: category.name,
            products: this.productQuery.selectFrontpageProducts$.pipe(
              switchMap((ids) => {
                return this.productQuery.selectMany(ids[i + 1]);
              }),
            ),
            link,
          };

          this.secondarySections.push(productSection);
        }
      });
  }
}
